import React, { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { X } from 'lucide-react';
import { toast } from 'react-toastify';
import { api } from '../services/api';
import { byRecentAssignment, fmtLastWorked } from '../utils/people';

// Pick people for an open shift, one at a time or a group at a time (owner,
// Sep 30 2026: "for open shift let me choose select people who I want or
// groups of people"). A group only speeds up picking — it turns into the
// people in it, and the shift keeps its own list. So changing a group later
// never changes a shift that was already offered.

interface PeopleGroup { id: number; name: string; member_ids: number[] }

// Same rule as the server's tidyPeople isAi: any name with the word "AI".
const isAiName = (name: any) => /\bai\b/i.test(String(name || ''));
// "Worked lately" = put on something in the last 3 weeks (the Tidy-up idle rule).
const WORKED_LATELY_DAYS = 21;

const hasPhone = (p: any) => !!String(p?.phone || '').trim();
const textsOff = (p: any) => p?.can_receive_shift_texts === 0 || p?.can_receive_shift_texts === false;
// Only people wearing the Employee hat can open the crew app and claim, and
// AI profiles never work a shift.
const canWorkShift = (p: any) => (p?.hats || []).includes('crew') && !isAiName(p?.name);

export default function PeoplePicker({ value, onChange }: { value: number[]; onChange: (ids: number[]) => void }) {
  const qc = useQueryClient();
  // Same query and cache as CreateJobModal's "Assign employees" chips.
  const { data: employeesData = [] } = useQuery('employees', () =>
    api.get('/employees').then(r => r.data.employees as any[])
  );
  const { data: groupsData = [] } = useQuery('people-groups', () =>
    api.get('/people-groups').then(r => r.data.groups as PeopleGroup[])
  );

  const picked = useMemo(() => new Set(value), [value]);
  // Anyone already picked stays in view, so they can be taken off even if
  // their hat changed since.
  const people = useMemo(() => (employeesData as any[])
    .filter((e: any) => canWorkShift(e) || picked.has(e.id))
    .sort(byRecentAssignment), [employeesData, picked]);
  const listed = useMemo(() => new Set(people.map((p: any) => p.id)), [people]);

  const groups = useMemo(() => {
    const today = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Chicago' });
    const floor = new Date(today + 'T12:00:00Z'); floor.setUTCDate(floor.getUTCDate() - WORKED_LATELY_DAYS);
    const floorISO = floor.toISOString().slice(0, 10);
    const lately = people
      .filter((p: any) => canWorkShift(p) && String(p.last_assigned_date || '').slice(0, 10) >= floorISO)
      .map((p: any) => p.id);
    return [
      { key: 'lately', id: null as number | null, name: 'Worked lately', ids: lately },
      ...(groupsData as PeopleGroup[]).map(g => ({
        key: `g${g.id}`, id: g.id as number | null, name: g.name,
        ids: (g.member_ids || []).filter(id => listed.has(id)),
      })),
    ].filter(g => g.id !== null || g.ids.length > 0);
  }, [people, groupsData, listed]);

  function toggle(id: number) {
    onChange(picked.has(id) ? value.filter(v => v !== id) : [...value, id]);
  }
  // A group adds its people (no doubles); tapped again once they're all in, it takes them off.
  function toggleGroup(ids: number[]) {
    if (!ids.length) return;
    if (ids.every(id => picked.has(id))) onChange(value.filter(v => !ids.includes(v)));
    else onChange(Array.from(new Set([...value, ...ids])));
  }

  const saveGroup = useMutation((body: { name: string; crew_member_ids: number[] }) =>
    api.post('/people-groups', body).then(r => r.data), {
    onSuccess: (_r: any, body) => { qc.invalidateQueries('people-groups'); toast.success(`Saved "${body.name}"`); },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not save that group'); },
  });
  const deleteGroup = useMutation((groupId: number) => api.delete(`/people-groups/${groupId}`).then(r => r.data), {
    onSuccess: () => { qc.invalidateQueries('people-groups'); },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not delete that group'); },
  });

  const pickedPeople = (employeesData as any[]).filter((e: any) => picked.has(e.id));
  const willText = pickedPeople.filter((p: any) => hasPhone(p) && !textsOff(p)).length;

  return (
    <div className="space-y-2">
      {/* Groups: the built-in "Worked lately" plus the ones he named */}
      {groups.length > 0 && (
        <div>
          <div className="flex flex-wrap gap-1.5">
            {groups.map(g => {
              const all = g.ids.length > 0 && g.ids.every(id => picked.has(id));
              return (
                <span key={g.key} className={`inline-flex items-center rounded-full border text-xs font-medium ${all ? 'bg-orange-100 text-orange-800 border-orange-300' : 'bg-white text-gray-600 border-dashed border-gray-300'}`}>
                  <button type="button" onClick={() => toggleGroup(g.ids)} disabled={!g.ids.length}
                    className={`press px-2.5 py-1 disabled:opacity-50 ${g.id !== null ? 'pr-1' : ''}`}
                    title={all ? 'Tap to take them all off' : 'Tap to add everyone in it'}>
                    {g.name} ({g.ids.length})
                  </button>
                  {g.id !== null && (
                    <button type="button"
                      onClick={() => { if (window.confirm(`Delete the group "${g.name}"? Shifts already offered keep their people.`)) deleteGroup.mutate(g.id as number); }}
                      disabled={deleteGroup.isLoading}
                      className="rounded-full p-0.5 mr-1 hover:bg-black/10" title="Delete this group">
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </span>
              );
            })}
          </div>
          <p className="text-[11px] text-gray-500 mt-1">Changing a group later doesn't change shifts already offered.</p>
        </div>
      )}

      {/* People, recent first */}
      <div className="flex flex-wrap gap-1.5">
        {people.map((p: any) => {
          const on = picked.has(p.id);
          const note = !hasPhone(p) ? 'no phone' : textsOff(p) ? 'texts off' : '';
          return (
            <button key={p.id} type="button" onClick={() => toggle(p.id)}
              className={`press px-2.5 py-1 text-xs rounded-full border font-medium ${on ? 'bg-brand-600 text-black border-brand-600' : 'bg-white text-gray-600 border-gray-200 hover:border-brand-400'}`}
              title={p.last_assigned_date ? `last worked ${fmtLastWorked(p.last_assigned_date)}` : 'never assigned yet'}>
              {p.name}
              {note && <span className={`ml-1 text-[10px] font-normal ${on ? 'text-black/70' : 'text-amber-700'}`}>· {note}</span>}
            </button>
          );
        })}
        {people.length === 0 && <span className="text-xs text-gray-500">Nobody with the Employee hat yet.</span>}
      </div>

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
        <button type="button" onClick={() => onChange(Array.from(new Set([...value, ...people.map((p: any) => p.id)])))}
          className="text-brand-700 hover:text-brand-900 font-medium">All</button>
        <button type="button" onClick={() => onChange([])} disabled={!value.length}
          className="text-gray-600 hover:text-gray-800 font-medium disabled:opacity-40">Clear</button>
        {pickedPeople.length > 1 && (
          <button type="button"
            onClick={() => {
              const name = window.prompt(`Name this group of ${pickedPeople.length} (for example "Project guys")`, '');
              if (name && name.trim()) saveGroup.mutate({ name: name.trim(), crew_member_ids: pickedPeople.map((p: any) => p.id) });
            }}
            disabled={saveGroup.isLoading}
            className="text-brand-700 hover:text-brand-900 font-medium disabled:opacity-40">
            {saveGroup.isLoading ? 'Saving…' : `Save these ${pickedPeople.length} as a group`}
          </button>
        )}
        <span className="ml-auto text-gray-600">
          {pickedPeople.length} picked · {willText} will get a text
        </span>
      </div>
    </div>
  );
}
